import Cookies from "js-cookie";
import userServices from "@/services/user";
import iamServices from "@/services/iam";
import featureFlagServices from "@/services/feature-flags";
import store from "@/store";
import router from "@/router";
import CONSTANTS from "@/config/constants";
import i18n from "@/utils/i18n";

export function getToken(key) {
  return Cookies.get(key);
}

export function setToken(key, token) {
  return Cookies.set(key, token);
}

export function deleteToken(key) {
  return Cookies.remove(key);
}

// Concurrent callers (e.g. App.vue's beforeMount and the router permission guard racing
// on the very first navigation) share this in-flight fetch instead of each firing their
// own request. Cleared once the fetch settles so later, genuinely separate calls (Login.vue/
// Otp.vue right after auth, Header.vue after a profile edit) always get a fresh one.
let userProfileLoadPromise = null;

export function getUserProfile(isFirstLoad = true) {
  if (userProfileLoadPromise) return userProfileLoadPromise;

  userProfileLoadPromise = (async () => {
    try {
      store.dispatch("updateIsLoadingPage", true);
      const res = await userServices.getUser();
      if (
        [
          "Login",
          "OtpVerify",
          "PortalAuthLayout",
          "Register",
          "ForgotPassword",
          "IamRegister",
        ].includes(router.history.current.name) &&
        isFirstLoad
      ) {
        router.push("/dashboard");
      }
      let userData = res?.data?.data;
      const permissionRes = await iamServices.getPermission();
      const permissionOfRole = permissionRes?.data?.data?.find(
        (item) => item?.name === userData?.iam_info?.role
      );
      const [responseServices, accountsStatusRes, featureFlags] = await Promise.all([
        userServices.getUserServices(),
        userServices.getAccountStatus(),
        featureFlagServices
          .getFeatureFlags()
          .then((flagsRes) => flagsRes?.data?.data ?? {})
          .catch(() => ({})),
      ]);
      userData = {
        ...userData,
        iam_info: {
          ...userData?.iam_info,
          permissions: permissionOfRole?.permissions,
        },
        services: responseServices.data.data,
        status: accountsStatusRes.data.data,
        feature_flags: featureFlags,
      };
      store.dispatch("updateUserProfile", userData);
      store.dispatch("updateIsLoadingPage", false);
    } catch (error) {
      store.dispatch("updateIsLoadingPage", false);
    } finally {
      userProfileLoadPromise = null;
    }
  })();

  return userProfileLoadPromise;
}

export function handlePushRouter(path) {
  if (this.$route.path !== path) this.$router.push(path);
}

export function handlePushRouterParams(path, params) {
  if (this.$route.path !== path)
    this.$router.push({ path: path, params: params });
}

export function handlePushRouterQuery(path, query) {
  if (this.$route.path !== path)
    this.$router.push({ path: path, query: query });
}

export function copyText(self, text) {
  self.$clipboard(text);
  self.$notify({
    title: self.$t("notification.success.title"),
    message: self.$t("notification.copied"),
    type: "success",
    customClass: "success",
  });
}

export function randomString(num = 5) {
  return Math.random()
    .toString(36)
    .slice(num * -1);
}

export function formatFlavor(flavor) {
  if (!flavor) return "";
  const num = flavor.match(/\d+/g).map(Number);
  return `${num?.[0]} CPU / ${num[1]}GB RAM`;
}

export function formatPrice(value) {
  if (value > 1000) {
    return `${currency(value.toFixed(0))}`;
  }
  return `${value?.toFixed(0)?.toString().replace(".", ",")}`;
}

export function currency(value, currency, decimals) {
  const DIGITS_RE = /(\d{3})(?=\d)/g;
  value = parseFloat(value);
  if (!isFinite(value) || (!value && value !== 0)) return "";
  currency = currency != null ? currency : "";
  decimals = decimals != null ? decimals : 0;
  const stringified = Math.floor(value).toFixed(decimals);
  const _int = decimals ? stringified.slice(0, -1 - decimals) : stringified;
  const i = _int.length % 3;
  const head = i > 0 ? _int.slice(0, i) + (_int.length > 3 ? "." : "") : "";
  const _float = decimals ? stringified.slice(-1 - decimals) : "";
  const sign = value < 0 ? "-" : "";
  return (
    sign + currency + head + _int.slice(i).replace(DIGITS_RE, "$1.") + _float
  );
}

export function flavorFormat(flavor) {
  if (!flavor) return "";
  const num = flavor.match(/\d+/g).map(Number);
  return `${num?.[0]} CPU, ${num[1]}GB RAM`;
}
export function invoiceStatus(status) {
  return CONSTANTS.INVOICE_STATUSES.find(
    (s) => s.value === status || s.value === "error"
  );
}

export function paymentMethod(method) {
  return CONSTANTS.PAYMENT_METHODS.find((s) => s.value === method);
}

export function paymentMethodReceipt(method) {
  return CONSTANTS.PAYMENT_METHODS_RECEIPTS.find((s) => s.value === method);
}

export function checkPermission(requirePermission, accountPermission) {
  return accountPermission.includes(requirePermission);
}

export function checkFeatureFlag(flagKey, featureFlags) {
  return !!featureFlags?.[flagKey];
}

export function generateCode(length = 10) {
  const charset = "abcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";

  for (let i = 0; i < length; i++) {
    const random = Math.floor(Math.random() * charset.length);
    result += charset[random];
  }

  return result;
}

// Random password valid for both LINUX_PASSWORD_PATTERN and WINDOWS_PASSWORD_PATTERN
export function generatePassword(length = 16) {
  const letters = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ";
  const digits = "23456789";
  const specials = "+-!#$%^&*()";
  const all = letters + digits + specials;
  const randomIndex = (max) => {
    const buf = new Uint32Array(1);
    window.crypto.getRandomValues(buf);
    return buf[0] % max;
  };
  const chars = [
    letters[randomIndex(letters.length)],
    digits[randomIndex(digits.length)],
    specials[randomIndex(specials.length)],
  ];
  while (chars.length < length) {
    chars.push(all[randomIndex(all.length)]);
  }
  // Fisher-Yates shuffle so the guaranteed characters are not always at the start
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomIndex(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}

export function getIntervalList() {
  return [
    { label: i18n.t("common.nHours", { n: 12 }), value: 12 * 60 * 60 },
    ...Array.from({ length: 7 }, (v, i) => i).map((item) => ({
      label: i18n.t("common.nDays", { n: item + 1 }),
      value: (item + 1) * 60 * 60 * 24,
    })),
  ];
}

export function formatBareMetalNodeConfig(node) {
  const cpu = Math.round(node.cpu);
  const ram = Math.round(node.ram);
  const disk_size = Math.round(node.disk_size);
  const name = node.name;
  return `${cpu} vCore - ${ram} GB RAM - ${disk_size} GB SSD`;
}

export function parseDataPagination(data, page = 1, limit = 10) {
  if (!data?.length) return [];
  const offset = (page - 1) * limit;
  return data.slice(offset).slice(0, limit);
}

export function parseBillingPlan(plan) {
  switch (plan) {
    case "subscription_1_months":
      return i18n.t("order.oneMonthPay");
    case "subscription_3_months":
      return i18n.t("order.threeMonthPay");
    case "subscription_6_months":
      return i18n.t("order.sixMonthPay");

    default:
      return i18n.t("order.twelveMonthPay");
  }
}


export function mappingAvailabilityZone(azName, availableZones) {
  return availableZones.find((zone) => zone.value === azName)?.label || azName;
}

export function getSafeRedirect(route) {
  const redirect = route?.query?.redirect;

  if (typeof redirect !== "string") {
    return null;
  }

  // Only allow continuing the Portal OIDC flow.
  if (
    redirect === "/sso/authorize" ||
    redirect.startsWith("/sso/authorize?")
  ) {
    return redirect;
  }

  return null;
}